import type { CSSProperties, ReactNode } from "react";

import type { IconName } from "@similarweb/icons";

import { useTranslate } from "@similarweb/i18n-provider";

import type { ButtonType } from "@similarweb/ui-components";
import { Button, IconButton } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";

const CHILIPIPER_BOOKING_BASE_URL = "https://similarweb.chilipiper.com/me";

export interface IBookMeetingButtonProps {
    /** Tracking label / parent component name (e.g. "PremiumFeatureHeader"). */
    label: string;
    /**
     * ChiliPiper personal-link slug for the account's representative
     * (e.g. "yakira-berkman"). Resolved by the backend from the AM/CSM email
     * and verified against ChiliPiper's FireRouter. The button opens
     * `https://similarweb.chilipiper.com/me/{slug}` in a new tab.
     */
    slug: string;
    className?: string;
    style?: CSSProperties;
    buttonType?: ButtonType;
    iconName?: IconName;
    children?: ReactNode;
    onClick?: () => void;
}

const buildBookingUrl = (slug: string): string =>
    `${CHILIPIPER_BOOKING_BASE_URL}/${encodeURIComponent(slug)}`;

/**
 * Opens the ChiliPiper personal scheduling page for a specific SimilarWeb
 * representative. Rendered alongside the regular Contact-Us CTA when the
 * account has a Salesforce Account Manager (or Account Success Manager) with a
 * resolved ChiliPiper slug.
 */
export const BookMeetingButton = ({
    label,
    slug,
    className,
    style,
    buttonType = "outlined",
    iconName,
    children,
    onClick,
}: IBookMeetingButtonProps) => {
    const { translate } = useTranslate();

    const handleClick = () => {
        allTrackers.dispatchTrackEvent("SI Hooks", "click", `book a meeting/${label}`);
        window.open(buildBookingUrl(slug), "_blank", "noopener,noreferrer");
        onClick?.();
    };

    const content = children ?? translate("si.common.book_a_meeting");

    if (iconName) {
        return (
            <IconButton
                className={className}
                style={style}
                type={buttonType}
                iconName={iconName}
                onClick={handleClick}
                data-automation={`book-a-meeting-${label}`}
            >
                {content}
            </IconButton>
        );
    }

    return (
        <Button
            className={className}
            style={style}
            type={buttonType}
            onClick={handleClick}
            data-automation={`book-a-meeting-${label}`}
        >
            {content}
        </Button>
    );
};
